import { useState } from 'react';
import useSubmitBenchmarkOffer, { SubmitBenchmarkOfferParams } from '@/enterprise/data/useSubmitBenchmarkOffer';
import useSubmitExpressAcceptOffer, {
  SubmitExpressAcceptOfferParams,
} from '@/enterprise/data/useSubmitExpressAcceptOffer';
import useSubmitPriceDiscoveryOffer, {
  SubmitPriceDiscoveryOfferInput,
} from '@/enterprise/data/useSubmitPriceDiscoveryOffer';
import useSubmitStaticOffer, { SubmitStaticOfferParams } from '@/enterprise/data/useSubmitStaticOffer';

export type SubmitValue =
  | {
      submitType: 'NAME_YOUR_RATE';
      makerOrganizationUuid: string;
      marketUuid: string;
      takerDivisionUuid: string;
      payload: SubmitPriceDiscoveryOfferInput;
    }
  | {
      submitType: 'BENCHMARK';
      makerOrganizationUuid: string;
      marketUuid: string;
      takerDivisionUuid: string;
      payload: SubmitBenchmarkOfferParams;
    }
  | {
      submitType: 'FIXED';
      makerOrganizationUuid: string;
      marketUuid: string;
      takerDivisionUuid: string;
      payload: SubmitStaticOfferParams;
    }
  | {
      submitType: 'EXPRESS_ACCEPT';
      makerOrganizationUuid: string;
      marketUuid: string;
      takerDivisionUuid: string;
      payload: SubmitExpressAcceptOfferParams;
    };

export interface OfferSubmissionSuccess {
  submitType: SubmitValue['submitType'] | 'unknown';
  makerOrganizationUuid: string;
  marketUuid: string;
  takerDivisionUuid: string;
  payload: SubmitPriceDiscoveryOfferInput | SubmitBenchmarkOfferParams | SubmitStaticOfferParams;
  submissionDurationMs: number;
  submissionBatchId: string;
}

export interface OfferSubmissionError {
  message: string;
  error: Error;
  submitType?: SubmitValue['submitType'] | 'unknown';
  payload?: SubmitPriceDiscoveryOfferInput | SubmitBenchmarkOfferParams | SubmitStaticOfferParams;
}

const useSubmitOffers = () => {
  const [loading, setLoading] = useState(false);
  const { mutateAsync: submitStaticOffer } = useSubmitStaticOffer();
  const { mutateAsync: submitBenchmarkOffer } = useSubmitBenchmarkOffer();
  const { mutateAsync: submitExpressAcceptOffer } = useSubmitExpressAcceptOffer();
  const { mutateAsync: submitPriceDiscoveryOffer } = useSubmitPriceDiscoveryOffer();

  /**
   * handles submission of a single offer and error/success collection
   * @param takerMarket the offer data to submit
   * @param submissionErrors array to collect submission errors
   * @param submissionSuccesses array to collect successful submissions
   * @param submissionBatchId unique identifier for this batch of submissions
   */
  const createOfferSubmission = async (
    takerMarket: SubmitValue,
    submissionErrors: OfferSubmissionError[],
    submissionSuccesses: OfferSubmissionSuccess[],
    submissionBatchId: string
  ): Promise<void> => {
    const startTime = performance.now();
    try {
      switch (takerMarket.submitType) {
        case 'EXPRESS_ACCEPT':
          await submitExpressAcceptOffer(takerMarket.payload);
          break;
        case 'NAME_YOUR_RATE':
          await submitPriceDiscoveryOffer(takerMarket.payload);
          break;
        case 'BENCHMARK':
          await submitBenchmarkOffer(takerMarket.payload);
          break;
        case 'FIXED':
          await submitStaticOffer(takerMarket.payload);
          break;
        default: {
          const exhaustiveCheck: never = takerMarket;
          submissionErrors.push({
            message: `Unknown pricing type: ${JSON.stringify(exhaustiveCheck)}`,
            error: new Error(`Unsupported pricing type: ${JSON.stringify(exhaustiveCheck)}`),
            submitType: 'unknown',
          });
          return;
        }
      }

      const endTime = performance.now();
      const submissionDurationMs = Math.round(endTime - startTime);

      submissionSuccesses.push({
        submitType: takerMarket.submitType,
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerDivisionUuid: takerMarket.takerDivisionUuid,
        payload: takerMarket.payload,
        submissionDurationMs,
        submissionBatchId,
      });
    } catch (error) {
      const errorMessages = {
        EXPRESS_ACCEPT: 'Failed to submit express accept offer',
        NAME_YOUR_RATE: 'Failed to submit price discovery offer',
        BENCHMARK: 'Failed to submit benchmark offer',
        FIXED: 'Failed to submit static offer',
      };
      submissionErrors.push({
        message: errorMessages[takerMarket.submitType],
        error: error as Error,
        submitType: takerMarket.submitType,
        payload: takerMarket.payload,
      });
    }
  };

  const submitOffers = async (
    selected: SubmitValue[]
  ): Promise<{ errors: OfferSubmissionError[]; successes: OfferSubmissionSuccess[]; submissionBatchId: string }> => {
    const submissionErrors: OfferSubmissionError[] = [];
    const submissionSuccesses: OfferSubmissionSuccess[] = [];
    const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    setLoading(true);

    try {
      const submissionPromises = selected.map((takerMarket) =>
        createOfferSubmission(takerMarket, submissionErrors, submissionSuccesses, submissionBatchId)
      );

      await Promise.all(submissionPromises);
    } catch (error) {
      submissionErrors.push({
        message: 'Error in overall offer submission process',
        error: error as Error,
      });
    } finally {
      setLoading(false);
    }

    return { errors: submissionErrors, successes: submissionSuccesses, submissionBatchId };
  };

  return {
    loading,
    submitOffers,
  };
};

export default useSubmitOffers;
